import { useState, useRef } from 'react';
import { Upload, X, Image } from 'lucide-react';
import { adminApi } from '../../utils/api';
import axios from 'axios';

const ALLOWED = /\.(jpe?g|png|gif|webp|svg)$/i;

const ImageUpload = ({ value, onChange, folder = 'kwa', label = 'Upload Image', accept = 'image/*' }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const fileRef = useRef();

  const handleFile = async (file) => {
    if (!file) return;
    if (!ALLOWED.test(file.name)) { setError('Only image files are allowed (JPG, PNG, GIF, WebP, SVG)'); return; }
    if (file.size > 20 * 1024 * 1024) { setError('Image must be under 20MB'); return; }

    setError('');
    setUploading(true);
    setProgress(0);

    try {
      const res = await adminApi.getPresignedUrl(folder, file.name, file.type);
      const { signedUrl, publicUrl } = res.data;

      await axios.put(signedUrl, file, {
        headers: { 'Content-Type': file.type, 'x-amz-acl': 'public-read' },
        onUploadProgress: (e) => {
          setProgress(Math.round((e.loaded / e.total) * 100));
        }
      });

      onChange(publicUrl);
    } catch (err) {
      setError('Upload failed. Please try again.');
      console.error(err);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <div className="space-y-2">
      {label && <label className="block text-sm font-medium text-gray-700">{label}</label>}

      {value ? (
        <div className="relative rounded-xl overflow-hidden border-2 border-gray-200">
          <img src={value} alt="Preview" className="w-full h-40 object-cover" />
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div
          onClick={() => !uploading && fileRef.current?.click()}
          onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
          onDragOver={e => e.preventDefault()}
          className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-cyan-400 hover:bg-cyan-50 transition-all"
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-3">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-cyan-500 h-2 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-sm text-gray-600 font-medium">Uploading... {progress}%</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Image className="w-8 h-8 text-gray-400" />
              <p className="text-sm text-gray-600 font-medium">Click or drag image here</p>
              <p className="text-xs text-gray-400">PNG, JPG, WebP up to 20MB — uploads directly to storage</p>
            </div>
          )}
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={e => handleFile(e.target.files[0])}
      />

      {error && <p className="text-red-500 text-sm">{error}</p>}
    </div>
  );
};

export default ImageUpload;
